import * as yup from 'yup';

import { PrepaySettings } from '../../../entities/prepaySettings';
import {
  DEFAULT_WIDGET_MESSAGE,
  DEFAULT_PREPAY_REQUIRED_TEXT,
  DEFAULT_PREPAY_REMINDER_TEXT,
  DEFAULT_PREPAY_CANCELLED_TEXT,
  REMINDER_DELAY_MIN,
  REMINDER_DELAY_MAX,
  AUTO_CANCEL_DELAY_MIN,
  AUTO_CANCEL_DELAY_MAX,
  WIDGET_MESSAGE_MAX_LENGTH,
  NOTIFICATION_TEMPLATE_MAX_LENGTH,
  REQUIRED_TEMPLATE_VARIABLES,
  MIN_PERSONS,
  AMOUNT_PER_GUEST_MIN,
  DEFAULT_WIDGET_MESSAGE_AUTO,
} from './constants';

export interface PrepaySettingsFormValues {
  isEnabled: boolean;
  paymentBank: PrepaySettings['paymentBank'];
  prepayAmountMode: PrepaySettings['prepayAmountMode'];
  reminderEnabled: boolean;
  reminderDelayMinutes: number;
  autoCancelEnabled: boolean;
  autoCancelDelayMinutes: number;
  widgetMessage: string;
  widgetMessageAuto: string;
  prepayRequiredTemplate: string;
  prepayReminderTemplate: string;
  prepayCancelledTemplate: string;
  amountPerPerson: PrepaySettings['amountPerPerson'];
  minPersons: PrepaySettings['minPersons'];
}

export const DEFAULT_FORM_VALUES: PrepaySettingsFormValues = {
  isEnabled: false,
  paymentBank: 'ALFA_BANK',
  prepayAmountMode: 'MANUAL',
  reminderEnabled: false,
  reminderDelayMinutes: REMINDER_DELAY_MIN,
  autoCancelEnabled: false,
  autoCancelDelayMinutes: AUTO_CANCEL_DELAY_MIN,
  widgetMessage: DEFAULT_WIDGET_MESSAGE,
  widgetMessageAuto: DEFAULT_WIDGET_MESSAGE_AUTO,
  prepayRequiredTemplate: DEFAULT_PREPAY_REQUIRED_TEXT,
  prepayReminderTemplate: DEFAULT_PREPAY_REMINDER_TEXT,
  prepayCancelledTemplate: DEFAULT_PREPAY_CANCELLED_TEXT,
  amountPerPerson: AMOUNT_PER_GUEST_MIN,
  minPersons: 0,
};

export const prepaySettingsValidationSchema = yup.object({
  isEnabled: yup.boolean(),
  paymentBank: yup.string().required('Выберите способ приема платежей'),
  prepayAmountMode: yup.string().required('Выберите способ расчета суммы'),
  reminderEnabled: yup.boolean(),
  reminderDelayMinutes: yup.number().when('reminderEnabled', {
    is: true,
    then: (schema) =>
      schema
        .min(REMINDER_DELAY_MIN, `Минимум ${REMINDER_DELAY_MIN} минут`)
        .max(REMINDER_DELAY_MAX, `Максимум ${REMINDER_DELAY_MAX} минут`)
        .required('Укажите время'),
    otherwise: (schema) => schema,
  }),
  autoCancelEnabled: yup.boolean(),
  autoCancelDelayMinutes: yup.number().when('autoCancelEnabled', {
    is: true,
    then: (schema) =>
      schema
        .min(AUTO_CANCEL_DELAY_MIN, `Минимум ${AUTO_CANCEL_DELAY_MIN} минут`)
        .max(AUTO_CANCEL_DELAY_MAX, `Максимум ${AUTO_CANCEL_DELAY_MAX} минут`)
        .required('Укажите время'),
    otherwise: (schema) => schema,
  }),
  widgetMessage: yup
    .string()
    .required('Сообщение обязательно')
    .max(WIDGET_MESSAGE_MAX_LENGTH, `Максимум ${WIDGET_MESSAGE_MAX_LENGTH} символов`),
  prepayRequiredTemplate: yup
    .string()
    .required('Шаблон уведомления обязателен')
    .max(NOTIFICATION_TEMPLATE_MAX_LENGTH, `Максимум ${NOTIFICATION_TEMPLATE_MAX_LENGTH} символов`)
    .test(
      'has-required-variables',
      `Шаблон должен содержать переменные: ${REQUIRED_TEMPLATE_VARIABLES.join(', ')}`,
      (value) => {
        if (!value) {
          return true;
        }
        return REQUIRED_TEMPLATE_VARIABLES.every((variable) => value.includes(variable));
      },
    ),
  prepayReminderTemplate: yup.string().when('reminderEnabled', {
    is: true,
    then: (schema) =>
      schema
        .required('Шаблон уведомления обязателен')
        .max(
          NOTIFICATION_TEMPLATE_MAX_LENGTH,
          `Максимум ${NOTIFICATION_TEMPLATE_MAX_LENGTH} символов`,
        )
        .test(
          'has-required-variables',
          `Шаблон должен содержать переменные: ${REQUIRED_TEMPLATE_VARIABLES.join(', ')}`,
          (value) => {
            if (!value) {
              return true;
            }
            return REQUIRED_TEMPLATE_VARIABLES.every((variable) => value.includes(variable));
          },
        ),
    otherwise: (schema) =>
      schema.max(
        NOTIFICATION_TEMPLATE_MAX_LENGTH,
        `Максимум ${NOTIFICATION_TEMPLATE_MAX_LENGTH} символов`,
      ),
  }),
  prepayCancelledTemplate: yup.string().when('autoCancelEnabled', {
    is: true,
    then: (schema) =>
      schema
        .required('Шаблон уведомления обязателен')
        .max(
          NOTIFICATION_TEMPLATE_MAX_LENGTH,
          `Максимум ${NOTIFICATION_TEMPLATE_MAX_LENGTH} символов`,
        ),
    otherwise: (schema) =>
      schema.max(
        NOTIFICATION_TEMPLATE_MAX_LENGTH,
        `Максимум ${NOTIFICATION_TEMPLATE_MAX_LENGTH} символов`,
      ),
  }),
});

export const mapDomainToForm = (domain: PrepaySettings): PrepaySettingsFormValues => ({
  isEnabled: domain.isEnabled,
  paymentBank: domain.paymentBank,
  prepayAmountMode: domain.prepayAmountMode,
  reminderEnabled: domain.reminder.isEnabled,
  reminderDelayMinutes: domain.reminder.delayMinutes,
  autoCancelEnabled: domain.autoCancel.isEnabled,
  autoCancelDelayMinutes: domain.autoCancel.delayMinutes,
  widgetMessage: domain.widgetMessage,
  widgetMessageAuto: domain.widgetMessageAuto,
  prepayRequiredTemplate: domain.notifications.prepayRequired.template,
  prepayReminderTemplate: domain.notifications.prepayReminder.template,
  prepayCancelledTemplate: domain.notifications.prepayCancelled.template,
  amountPerPerson: domain.amountPerPerson,
  minPersons: domain.minPersons,
});

export const mapFormToDomain = (form: PrepaySettingsFormValues): PrepaySettings => ({
  isEnabled: form.isEnabled,
  paymentBank: form.paymentBank,
  prepayAmountMode: form.prepayAmountMode,
  widgetMessage: form.widgetMessage,
  widgetMessageAuto: form.widgetMessageAuto,
  amountPerPerson: form.amountPerPerson || AMOUNT_PER_GUEST_MIN,
  minPersons: form.minPersons || MIN_PERSONS,
  reminder: {
    isEnabled: form.reminderEnabled,
    delayMinutes: Number(form.reminderDelayMinutes) || 0,
  },
  autoCancel: {
    isEnabled: form.autoCancelEnabled,
    delayMinutes: Number(form.autoCancelDelayMinutes) || 0,
  },
  notifications: {
    prepayRequired: {
      type: 'PREPAYMENT_REQUIRED',
      isEnabled: Boolean(form.prepayRequiredTemplate.trim()),
      template: form.prepayRequiredTemplate,
    },
    prepayReminder: {
      type: 'PREPAYMENT_REMINDER',
      isEnabled: form.reminderEnabled && Boolean(form.prepayReminderTemplate.trim()),
      template: form.prepayReminderTemplate,
    },
    prepayCancelled: {
      type: 'PREPAYMENT_CANCELLED',
      isEnabled: form.autoCancelEnabled && Boolean(form.prepayCancelledTemplate.trim()),
      template: form.prepayCancelledTemplate,
    },
  },
});
